import { Modal } from "../../UI/Modal/Modal.tsx";
import { Dialog } from "@headlessui/react";
import { OutlinedButton } from "@normasteaching/norma-ui";
import { useMessageController } from "../../UI/useMessageController.ts";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import copy from "copy-to-clipboard";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { NormasTeachingBundleDTO } from "../../ProductService/NormasTeachingBundle/useNormasTeachingBundleById.ts";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";

function BundleDetailsModal({
  bundle,
  onClose,
}: {
  bundle: NormasTeachingBundleDTO;
  onClose: () => void;
}) {
  const { showMessage, message, open } = useMessageController();
  const { products } = useAllNormasTeachingProducts();

  const getProductById = (
    productId: string,
  ): NormasTeachingProductDTO | undefined => {
    return products.find((p) => p._id === productId);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Bundle"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-4xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <SuccessMessage open={open} message={message} />
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-4xl">
        {/* Informazioni generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Bundle ID:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600"
                onClick={() => {
                  copy(bundle._id);
                  showMessage({
                    message: "ID del bundle copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {bundle._id}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Nome Bundle:</span>
              <p className="text-gray-600">{bundle.bundleName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Nome Variante:</span>
              <p className="text-gray-600">{bundle.variantBundleName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Prezzo:</span>
              <p className="text-gray-600">
                €{(bundle.priceInCents.amount / 100).toFixed(2)}{" "}
                {bundle.priceInCents.currency.toUpperCase()}
              </p>
            </div>
            {bundle.analyticsTag && (
              <div className="col-span-2">
                <span className="font-medium text-gray-700">
                  Analytics Tag:
                </span>
                <p className="text-gray-600">{bundle.analyticsTag}</p>
              </div>
            )}
          </div>
        </div>

        {/* Descrizione Bundle */}
        {bundle.bundleDescription && bundle.bundleDescription.trim() !== "" && (
          <ExpandableCard title="Descrizione Bundle">
            <div className="prose prose-sm max-w-none mb-4 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {bundle.bundleDescription}
              </ReactMarkdown>
            </div>
          </ExpandableCard>
        )}

        {/* Active Campaign Tags */}
        {(bundle.purchaseAcTags || bundle.refundAcTags) && (
          <ExpandableCard title="Active Campaign Tags">
            {bundle.purchaseAcTags && (
              <div>
                <span className="font-medium text-gray-700">
                  Tag post acquisto:
                </span>
                {bundle.purchaseAcTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bundle.purchaseAcTags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 mt-1">Nessun tag configurato</p>
                )}
              </div>
            )}
            {bundle.refundAcTags && (
              <div>
                <span className="font-medium text-gray-700">
                  Tag post rimborso:
                </span>
                {bundle.refundAcTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bundle.refundAcTags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 mt-1">Nessun tag configurato</p>
                )}
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Persone e Prodotti nel Bundle */}
        {bundle.bundleProducts && bundle.bundleProducts.length > 0 && (
          <ExpandableCard title="Persone e Prodotti nel Bundle">
            {bundle.bundleProducts.map((personBundle, personIndex) => {
              const personTotal = personBundle.products.reduce(
                (sum, product) => sum + product.priceInCents.amount,
                0,
              );

              return (
                <div
                  key={personIndex}
                  className="bg-gray-50 p-4 rounded-md space-y-3"
                >
                  <h4 className="font-semibold text-gray-800">
                    Persona {personIndex + 1}
                  </h4>
                  <div className="space-y-2 pl-3">
                    {personBundle.products.map(
                      (bundleProduct, productIndex) => {
                        const productInfo = getProductById(bundleProduct.id);
                        return (
                          <div
                            key={productIndex}
                            className="bg-white p-3 rounded border border-gray-200"
                          >
                            <div className="space-y-1">
                              <div>
                                <span className="text-sm font-medium text-gray-700">
                                  Prodotto:
                                </span>{" "}
                                <span className="text-sm text-gray-600">
                                  {productInfo?.productName ||
                                    "Prodotto sconosciuto"}
                                  {productInfo?.variantProductName &&
                                    ` - ${productInfo.variantProductName}`}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-700">
                                  ID:
                                </span>{" "}
                                <span
                                  className="text-sm text-gray-600 cursor-pointer hover:text-blue-600"
                                  onClick={() => {
                                    copy(bundleProduct.id);
                                    showMessage({
                                      message:
                                        "ID del prodotto copiato negli appunti!",
                                      timeoutMs: 4000,
                                    });
                                  }}
                                >
                                  {bundleProduct.id}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-700">
                                  Prezzo:
                                </span>{" "}
                                <span className="text-sm text-gray-600">
                                  €
                                  {(
                                    bundleProduct.priceInCents.amount / 100
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                  <div className="pt-2 border-t border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">
                        Totale Persona {personIndex + 1}:
                      </span>
                      <span className="font-semibold text-gray-800">
                        €{(personTotal / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </ExpandableCard>
        )}

        {/* Configurazione Stripe */}
        {bundle.stripeConfig && (
          <ExpandableCard title="Configurazione Stripe">
            <div>
              <span className="font-medium text-gray-700">Rate abilitate:</span>
              <p className="text-gray-600">
                {bundle.stripeConfig.installmentsEnabled ? "Sì" : "No"}
              </p>
            </div>
            {bundle.stripeConfig.allowedTerms &&
              bundle.stripeConfig.allowedTerms.length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">
                    Numero rate consentite:
                  </span>
                  <p className="text-gray-600">
                    {bundle.stripeConfig.allowedTerms.join(", ")}
                  </p>
                </div>
              )}
            {bundle.stripeConfig.succeededUrl && (
              <div>
                <span className="font-medium text-gray-700">URL successo:</span>
                <p className="text-gray-600 break-all">
                  {bundle.stripeConfig.succeededUrl}
                </p>
              </div>
            )}
            {bundle.stripeConfig.failedUrl && (
              <div>
                <span className="font-medium text-gray-700">
                  URL fallimento:
                </span>
                <p className="text-gray-600 break-all">
                  {bundle.stripeConfig.failedUrl}
                </p>
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Configurazione HeyLight */}
        {bundle.heylightConfig && (
          <ExpandableCard title="Configurazione HeyLight">
            {bundle.heylightConfig.allowedTerms &&
              bundle.heylightConfig.allowedTerms.length > 0 && (
                <div>
                  <span className="font-medium text-gray-700">
                    Numero rate consentite:
                  </span>
                  <p className="text-gray-600">
                    {bundle.heylightConfig.allowedTerms.join(", ")}
                  </p>
                </div>
              )}
            {bundle.heylightConfig.successUrl && (
              <div>
                <span className="font-medium text-gray-700">URL successo:</span>
                <p className="text-gray-600 break-all">
                  {bundle.heylightConfig.successUrl}
                </p>
              </div>
            )}
            {bundle.heylightConfig.failureUrl && (
              <div>
                <span className="font-medium text-gray-700">
                  URL fallimento:
                </span>
                <p className="text-gray-600 break-all">
                  {bundle.heylightConfig.failureUrl}
                </p>
              </div>
            )}
          </ExpandableCard>
        )}

        {/* Pulsante chiudi */}
        <div className="pt-4 border-t">
          <OutlinedButton onClick={onClose} label="Chiudi" />
        </div>
      </div>
    </Modal>
  );
}

export default BundleDetailsModal;
